import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import styles from "./Portals.module.css";

interface PortalEntry {
  id: string;
  name: string;
  hubspot_portal_id: string;
  connected_by: string;
  created_at: number;
  nickname?: string;
}

export default function Portals() {
  const [portals, setPortals] = useState<PortalEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editValue, setEditValue] = useState("");

  function load() {
    Promise.all([
      fetch("/api/portals").then((r) => r.json() as Promise<PortalEntry[]>),
      fetch("/api/nicknames").then(
        (r) => r.json() as Promise<Record<string, string>>,
      ),
    ])
      .then(([portalList, nicknames]) => {
        const merged = portalList.map((p) => ({
          ...p,
          nickname: nicknames[p.hubspot_portal_id] || undefined,
        }));
        setPortals(merged);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }

  useEffect(() => {
    load();
  }, []);

  function handleDelete(id: string) {
    if (
      !confirm(
        "Remove this portal? This also uninstalls Henry Intelligence from the HubSpot account.",
      )
    )
      return;
    fetch(`/api/portals/${id}`, { method: "DELETE" })
      .then(
        (r) =>
          r.json() as Promise<{
            hubspotUninstalled?: boolean;
            hubspotAlreadyUninstalled?: boolean;
            hubspotError?: string;
          }>,
      )
      .then((result) => {
        setPortals((prev) => prev.filter((p) => p.id !== id));
        if (result.hubspotUninstalled === false) {
          alert(
            `Removed from this dashboard, but HubSpot did not confirm the app was uninstalled from the account: ${
              result.hubspotError ?? "unknown error"
            }\n\nYou may need to remove it manually in that portal under Settings > Integrations > Connected Apps.`,
          );
        }
      })
      .catch(() => {});
  }

  function startEdit(portal: PortalEntry) {
    setEditingId(portal.hubspot_portal_id);
    setEditValue(portal.nickname ?? "");
  }

  function commitEdit(portalId: string) {
    const trimmed = editValue.trim();
    setPortals((prev) =>
      prev.map((p) =>
        p.hubspot_portal_id === portalId
          ? { ...p, nickname: trimmed || undefined }
          : p,
      ),
    );
    fetch("/api/nicknames", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ [portalId]: trimmed }),
    }).catch(() => {});
    setEditingId(null);
  }

  return (
    <div className={styles.page}>
      <div className={styles.topNav}>
        <Link to="/" className={styles.backBtn}>
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            aria-hidden="true"
          >
            <path
              d="M10 3L5 8l5 5"
              stroke="currentColor"
              strokeWidth="1.75"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          Home
        </Link>
        <a href="/api/portals/oauth/init" className={styles.connectBtn}>
          + Connect portal
        </a>
      </div>

      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <span className={styles.sectionLabel}>Connected Portals</span>
        </div>

        <div className={styles.card}>
          {loading ? (
            <p className={styles.empty}>Loading…</p>
          ) : portals.length === 0 ? (
            <p className={styles.empty}>No portals connected yet.</p>
          ) : (
            <ul className={styles.list}>
              {portals.map((p) => (
                <li key={p.id} className={styles.row}>
                  <div className={styles.rowMain}>
                    {editingId === p.hubspot_portal_id ? (
                      <input
                        className={styles.nicknameInput}
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        onBlur={() => commitEdit(p.hubspot_portal_id)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter")
                            commitEdit(p.hubspot_portal_id);
                          if (e.key === "Escape") setEditingId(null);
                        }}
                        autoFocus
                        placeholder={p.name}
                      />
                    ) : (
                      <span className={styles.portalName}>
                        {p.nickname ?? p.name}
                      </span>
                    )}
                    <span className={styles.portalId}>
                      {p.hubspot_portal_id}
                    </span>
                  </div>
                  <div className={styles.rowMeta}>
                    <span className={styles.metaItem}>{p.connected_by}</span>
                    <span className={styles.metaItem}>
                      {new Date(p.created_at * 1000).toLocaleDateString()}
                    </span>
                  </div>
                  <button
                    className={styles.renameBtn}
                    onClick={() => startEdit(p)}
                    title="Rename portal"
                  >
                    <svg
                      width="12"
                      height="12"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                    </svg>
                  </button>
                  <a
                    className={styles.reauthBtn}
                    href="/api/portals/oauth/init"
                    title="Re-authenticate to refresh tokens or pick up new scopes"
                  >
                    Re-auth
                  </a>
                  <button
                    className={styles.deleteBtn}
                    onClick={() => handleDelete(p.id)}
                  >
                    Remove
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>
    </div>
  );
}
